<template>
  <router-view />
</template>

<script>
export default {
  name: 'App',
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  /* Vintage gallery palette: warm paper grounds, brown-black ink and a
     rust accent borrowed from old frames and terracotta. */
  --ink:       #2B2420;
  --charcoal:  #4A3F38;
  --stone:     #7D6E60;
  --warm-grey: #C4B6A3;
  --linen:     #E5D9C6;
  --paper:     #EFE6D6;
  --canvas:    #F8F3EA;
  --blush:     #EAD3C0;
  --sky:       #DEDCC8;
  --amber:     #A4492B;
  /* Slightly deeper rust for small white text on a filled bar (AA 4.5:1). */
  --amber-deep: #93401F;
  --ochre:     #C29A4A;
  --sage:      #8E9478;

  --font:         'DM Sans', system-ui, sans-serif;
  --font-display: 'Cormorant Garamond', 'Times New Roman', serif;
  --radius-btn: 0;
  --max-w:     132rem;
  --gap:       8px;
  --gap-sm:    4px;
}

html {
  font-size: 62.5%;
  -webkit-text-size-adjust: 100%;
}

body {
  font-family: var(--font);
  color: var(--ink);
  background: var(--canvas);
  -webkit-font-smoothing: antialiased;
}

a { text-decoration: none; }
button { font-family: inherit; }
img { display: block; max-width: 100%; }

h1, h2, h3 { font-family: var(--font-display); font-weight: 500; }

/* Small uppercase label used above headings across the site. */
.eyebrow {
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--amber);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  white-space: nowrap;
  border: 0;
}

/* ── Buttons (shared across views/components) ────────────── */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem 2.8rem;
  border-radius: var(--radius-btn, 40px);
  font-size: 1.2rem;
  font-weight: 500;
  font-family: var(--font);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;
  color: inherit;
}

.btn--white {
  background: var(--canvas);
  color: var(--ink);
  border-color: var(--canvas);
}
.btn--white:hover { background: var(--linen); border-color: var(--linen); }

.btn--ghost {
  background: transparent;
  color: var(--canvas);
  border-color: rgba(255,255,255,0.45);
}
.btn--ghost:hover { border-color: var(--canvas); background: rgba(255,255,255,0.08); }

.btn--outline-light {
  background: transparent;
  color: var(--canvas);
  border-color: rgba(255,255,255,0.55);
}
.btn--outline-light:hover { background: var(--canvas); color: var(--ink); }

.btn--dark {
  background: var(--ink);
  color: var(--canvas);
  border-color: var(--ink);
}
.btn--dark:hover { background: var(--charcoal); border-color: var(--charcoal); }

.btn--rust {
  background: var(--amber);
  color: var(--canvas);
  border-color: var(--amber);
}
.btn--rust:hover { background: var(--amber-deep); border-color: var(--amber-deep); }

/* ── Page-load fade ───────────────────────────────────────
   The header eases in, then each top-level section of <main> rises in
   turn. Fill mode is "backwards" so nothing is held at the end state
   and sections keep control of their own opacity/transform. The hero
   staggers its own pieces (HeroSection.vue). */
@keyframes fade-in {
  from { opacity: 0; }
}

@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(1.6rem); }
}

.announcement-bar,
.site-nav { animation: fade-in 0.8s ease backwards; }

main > :not(.hero) { animation: fade-in-up 0.9s cubic-bezier(0.2, 0.7, 0.2, 1) backwards; }
main > :nth-child(1) { animation-delay: 0.1s; }
main > :nth-child(2) { animation-delay: 0.35s; }
main > :nth-child(3) { animation-delay: 0.5s; }
main > :nth-child(n + 4) { animation-delay: 0.65s; }

@media (prefers-reduced-motion: reduce) {
  .announcement-bar,
  .site-nav,
  main > * { animation: none; }
}
</style>
